<script setup lang="ts">
//#region imports
import { computed, ref, useTemplateRef, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import _ from '@/setup/lodash';
import { useApi } from '@/api';
import { useError } from '@/composables/useError';
import ColorTags from '@/components/ColorTags.vue';
import { useTypedStore } from '@/store';
import { usePopupManager } from '@/components/PopupManager.vue';
import type { ToErrorMap } from '@/types/core';
import type { GetAffiliateNotesResponse, PostAffiliateNotesData, PutAffiliateNotesByIdData } from '@/types/openapi-ts';
//#endregion

//#region types
export type AffiliateNotesRow = GetAffiliateNotesResponse['payload'][number];
type AffiliateNotesPermissions = NonNullable<GetAffiliateNotesResponse['misc']>['permissions'];

interface Props {
  affiliateId?: string,
  addNotePermission?: boolean,
}
//#endregion

//#region define
type FormErrors = ToErrorMap<PostAffiliateNotesData['body'] & PutAffiliateNotesByIdData['body']>;

type NewNote = {
  id?: string;
  pinned?: boolean;
  color: string;
  text: string;
};

const props = withDefaults(defineProps<Props>(), {
  affiliateId: '',
  addNotePermission: false,
});
//#endregion

//#region const
const availableNoteTags = ['white', 'green', 'red', 'yellow'];
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const store = useTypedStore();
const { errors } = useError<NonNullable<FormErrors>>();
const popupManager = usePopupManager();
//#endregion

//#region state
const isLoadNotes = ref(false);
const isAddingNewNote = ref(false);
const currentNotesType = ref('all');

const noteTextareaRef = useTemplateRef('noteTextareaRef');
const noteTextareaNewRef = useTemplateRef('noteTextareaNewRef');
const scrollRef = useTemplateRef('scrollRef');

const notes = ref<AffiliateNotesRow[]>([]);
const notesPermissions = ref<AffiliateNotesPermissions>({});
const editingNote = ref(-1);
const newNote = ref<NewNote>({
  text: '',
  color: 'null',
});
//#endregion

//#region computed
const noteTags = computed(() => store.getters['misc/noteTags']);
const inputColor = computed(() =>{
  return _.find(noteTags.value, { id: newNote.value.color })?.content || 'white';
});

const notesTypes = computed(() => {
  return [
    {
      value: 'all',
      name: t('affiliates.cardView.notes.types.all'),
    },
    {
      value: 'user',
      name: t('affiliates.cardView.notes.types.user'),
    },
    {
      value: 'system',
      name: t('affiliates.cardView.notes.types.system'),
    },
  ];
});

const notesList = computed(() => {
  if (!_.isEmpty(notes.value)) {
    if (currentNotesType.value === 'all') {
      return notes.value;
    }
    const typeNotesList = _.filter(notes.value, ['type', currentNotesType.value]);
    return typeNotesList.length > 0 ? typeNotesList : false;
  }
  return false;
});
//#endregion

//#region methods
function getNotes(id: string) {
  api.getAffiliateNotes(id).then((response) => {
    notes.value = _.cloneDeep(response.data.payload);
    notesPermissions.value = _.cloneDeep(response.data.misc!.permissions);
    isLoadNotes.value = true;
    cancelNote();
  });
}

function handlerAddNote() {
  isAddingNewNote.value = true;
  nextTick(() => {
    noteTextareaNewRef.value?.focus?.();
  });

  if(scrollRef.value) {
    scrollRef.value.scrollTop = 0;
  }
  editingNote.value = -1;
}

function saveNote() {
  const q = {
    affiliate_id: props.affiliateId,
    text: newNote.value.text,
    pinned: false,
    tag_ids: [],
  } as PostAffiliateNotesData['body'];
  if (newNote.value.color !== 'null') {
    q.tag_ids.push(newNote.value.color);
  }
  errors.value = {};
  api.postAffiliateNote(q).then(() => {
    getNotes(props.affiliateId);
  })
  .catch((error) => {
    errors.value = _.cloneDeepWith(error.data.errors);
  });
}

function cancelNote() {
  isAddingNewNote.value = false;
  editingNote.value = -1;
  errors.value = {};
  newNote.value = {
    text: '',
    color: 'null',
  };
}

function updateNote(note: NewNote) {
  const q: PutAffiliateNotesByIdData['body'] = {
    text: newNote.value.text,
    pinned: newNote.value.pinned!,
    tag_ids: [],
  };
  if (note.color !== 'null') {
    q.tag_ids.push(note.color);
  }
  updateNoteRequest(note.id!, q);
}

function deleteNoteComponent(note: AffiliateNotesRow) {
  api
    .deleteAffiliateNote(note.id)
    .then(() => {
      getNotes(props.affiliateId);
    });
}

function deleteNote(note: AffiliateNotesRow) {
  const context = popupManager.confirm.open({
    data: {
      title: t('affiliates.confirm.delete_note_title'),
      message: t('affiliates.confirm.delete_note_text'),
      bodyIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('affiliates.confirm.delete_note_action'),
      confirmType: 'negative',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        api.deleteAffiliateNote(note.id).then(() => {
          getNotes(props.affiliateId);
          confirm.handleClose();
        });
      },
    },
  });
}

function pinNote(note: AffiliateNotesRow) {
  const q: PutAffiliateNotesByIdData['body'] = {
    text: note.text,
    pinned: note.pinned,
    tag_ids: [],
  };
  if (note.tags[0]) {
    q.tag_ids.push(note.tags[0].id);
  }
  q.pinned = !q.pinned;
  updateNoteRequest(note.id, q);
}

function editNote(note_index: number, item: AffiliateNotesRow) {
  isAddingNewNote.value = false;
  editingNote.value = note_index;
  newNote.value = {
    id: item.id,
    text: item.text,
    pinned: item.pinned,
    color: 'null',
  };

  if (item.tags[0]) {
    newNote.value.color = item.tags[0].id;
  }

  nextTick(() => {
    noteTextareaRef.value?.[editingNote.value]?.focus?.();
  });
}

function updateNoteRequest(id: string, params: PutAffiliateNotesByIdData['body']) {
  errors.value = {};
  api.putAffiliateNote(id, params)
    .then(() => {
      getNotes(props.affiliateId);
    })
    .catch((error) => {
      errors.value = _.cloneDeepWith(error.data.errors);
    });
}

function isEditNotesPermission(id: string) {
  return !!notesPermissions.value[id]?.edit;
}
//#endregion

//#region watch
watch(() => props.affiliateId, (value) => {
  if(value) {
    getNotes(value);
  }
}, {immediate: true});
//#endregion

defineExpose({
  getNotes,
});
</script>

<template>
  <div class="notes">
    <div class="t:space-y-8">
      <CardTitle
        :title="$t(`affiliates.cardView.notes.title`)"
      >
        <UiAction
          v-if="!isAddingNewNote && addNotePermission && currentNotesType !== 'system'"
          icon="plus"
          :action-name="$t(`affiliates.cardView.notes.addNote`)"
          @click="handlerAddNote"
        />
      </CardTitle>
      <UiTabs
        v-model="currentNotesType"
        :list="notesTypes"
      />
    </div>
    <div
      ref="scrollRef"
      class="notes__wrapper t:space-y-8"
    >
      <div
        v-if="isAddingNewNote"
        class="t:space-y-8"
      >
        <UiTextarea
          ref="noteTextareaNewRef"
          v-model="newNote.text"
          :class="[`notes__textarea_${inputColor}`]"
          :placeholder="$t(`affiliates.cardView.notes.newNote`)"
          :rows="3"
          :error="errors.text"
        />
        <div class="t:flex t:items-center t:justify-between">
          <ColorTags
            v-model="newNote.color"
            :available-note-tags="availableNoteTags"
          />
          <div class="t:flex t:space-x-8">
            <UiButton
              type="negative"
              icon="times"
              @click="cancelNote"
            >
              {{ $t(`team_popup.cancel_button`) }}
            </UiButton>
            <UiButton
              filled
              icon="check"
              @click="saveNote"
            >
              {{ $t(`team_popup.save_button`) }}
            </UiButton>
          </div>
        </div>
      </div>
      <template v-if="notesList">
        <template
          v-for="(item, index) in notesList"
          :key="index"
        >
          <div
            v-if="editingNote !== index"
            :class="item.tags[0] ? 'notes__item_'+item.tags[0].content : ''"
            class="notes__item"
          >
            <div class="t:flex t:items-center t:justify-between">
              <div class="notes__item-author">
                <UiIcon
                  :name="item.type === 'user' ? 'user' : 'cog'"
                  color="inherit"
                  substyle="far"
                />
                {{ item.type === 'user' ? item.admin_name : $t(`affiliates.cardView.info.system`) }}
              </div>
              <div
                v-if="isEditNotesPermission(item.id) && item.type !== 'system'"
                class="notes__item-actions"
              >
                <UiAction
                  :icon="item.pinned ? 'unlink' : 'map-pin'"
                  color="inherit"
                  @click="pinNote(item)"
                />
                <UiAction
                  icon="pen"
                  color="inherit"
                  @click="editNote(index, item)"
                />
                <UiAction
                  icon="times"
                  color="inherit"
                  @click="deleteNote(item)"
                />
              </div>
            </div>
            <div class="notes__item-date">
              <UiIcon
                name="calendar-days"
                color="inherit"
                substyle="far"
              />
              {{ $filters.formatDate3(item.created_at!) }}
              <UiIcon
                v-if="item.pinned"
                name="map-pin"
                color="inherit"
                substyle="far"
              />
            </div>
            <div class="notes__item-text">
              {{ item.text }}
            </div>
          </div>
          <div
            v-else
            class="t:space-y-8"
          >
            <UiTextarea
              ref="noteTextareaRef"
              v-model="newNote.text"
              :class="[`notes__textarea_${inputColor}`]"
              :placeholder="$t(`affiliates.cardView.notes.newNote`)"
              :rows="3"
              :error="errors.text"
            />
            <div class="t:flex t:items-center t:justify-between">
              <ColorTags
                v-model="newNote.color"
                :available-note-tags="availableNoteTags"
              />
              <div class="t:flex t:space-x-8">
                <UiButton
                  type="negative"
                  icon="times"
                  @click="cancelNote"
                >
                  {{ $t(`team_popup.cancel_button`) }}
                </UiButton>
                <UiButton
                  filled
                  icon="check"
                  @click="updateNote(newNote)"
                >
                  {{ $t(`team_popup.save_button`) }}
                </UiButton>
              </div>
            </div>
          </div>
        </template>
      </template>
      <template v-else>
        <div class="notes__empty">
          <UiIcon
            :size="32"
            name="folder-open"
            substyle="fal"
            color="inherit"
            class="t:mt-24 t:mb-8"
          />
          {{ $t(`affiliates.cardView.notes.noNotes`) }}
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.notes {
  $root: &;

  min-width: 284px;
  max-width: 324px;

  flex-grow: 1;
  flex-shrink: 1;
  overflow-y: auto;
  &__wrapper {
    overflow: auto;
    max-height: calc(100% - 72px);
    padding-top: 8px;
  }

  &__textarea {
    &_green {
      .ui-textarea__input{
        --el-input-bg-color: var(--background-positive-subtle);
      }
    }

    &_red {
      .ui-textarea__input{
        --el-input-bg-color: var(--background-negative);
        --el-input-text-color: var(--text-body-invert);
        --el-input-placeholder-color: var(--text-body-invert);
      }
    }

    &_yellow {
      .ui-textarea__input{
        --el-input-bg-color: var(--background-warning-subtle);
      }
    }
  }

  &__item{
    display: flex;
    padding: 12px;
    flex-direction: column;
    gap: 4px;
    border-radius: 8px;
    color: var(--text-primary);
    border: 1px solid var(--border-divider);

    &_green {
      background-color: var(--background-positive-subtle);
      border: 1px solid transparent;
    }
    &_red {
      background-color: var(--background-negative);
      color: var(--text-body-invert);
      border: 1px solid transparent;

      #{$root}__item-author,
      #{$root}__item-date,
      #{$root}__item-text {
        color: var(--text-body-invert);
      }
    }
    &_yellow {
      background-color: var(--background-warning-subtle);
      border: 1px solid transparent;
    }

    &:hover {
      #{$root}__item-actions{
        display: flex;
      }
    }
  }

  &__item-author {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--text-body-secondary);
    font-size: 12px;
  }
  &__item-actions {
    display: none;
    align-items: center;
    gap: 12px;
  }
  &__item-date {
    display: flex;
    align-items: center;
    font-size: 12px;
    gap: 4px;
  }
  &__item-text {
    color: var(--text-body);
    font-size: 14px;
    line-height: 18px;

    white-space: pre-line;
    word-wrap: break-word;
  }

  &__empty{
    padding: 10px 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    font-size: 20px;
    color: var(--text-body-tertiary);
    width: 100%;
  }
}
</style>
